import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ChemicalCalculator = ({ onCalculationComplete }) => {
  const [formData, setFormData] = useState({
    chemical: '',
    applicationMethod: 'foliar-spray',
    cropType: '',
    treatmentArea: '',
    areaUnit: 'sqft',
    tankSize: '1',
    concentrationTarget: '',
    waterSource: 'municipal',
    phLevel: ''
  });

  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const chemicals = [
    { 
      value: 'zerotol-2', 
      label: 'Zerotol 2.0 (Hydrogen Peroxide)', 
      type: 'fungicide',
      rates: { foliar: '2-4', drench: '1-2', dip: '0.5-1' },
      phOptimal: '6.5-7.0'
    },
    { 
      value: 'azadirachtin', 
      label: 'Azadirachtin (Neem)', 
      type: 'insecticide',
      rates: { foliar: '1-2', drench: '0.5-1', dip: '0.25-0.5' },
      phOptimal: '6.0-7.0'
    },
    { 
      value: 'cease', 
      label: 'Cease (Bacillus subtilis)', 
      type: 'biofungicide',
      rates: { foliar: '2-6', drench: '2-4', dip: '1-2' },
      phOptimal: '6.0-8.0'
    },
    { 
      value: 'rootshield-plus', 
      label: 'RootShield Plus (Trichoderma)', 
      type: 'biocontrol',
      rates: { foliar: '1-2', drench: '0.5-1', dip: '0.25-0.5' },
      phOptimal: '5.5-7.5'
    },
    { 
      value: 'suffoil-x', 
      label: 'SuffOil-X (Mineral Oil)', 
      type: 'insecticide',
      rates: { foliar: '1-2', drench: '0.5', dip: '0.25' },
      phOptimal: '6.0-7.5'
    },
    { 
      value: 'bt-thuricide', 
      label: 'Bt Thuricide (Bacillus thuringiensis)', 
      type: 'bioinsecticide',
      rates: { foliar: '1-4', drench: '1-2', dip: '0.5-1' },
      phOptimal: '6.5-7.5'
    }
  ];

  const applicationMethods = [
    { value: 'foliar-spray', label: 'Foliar Spray' },
    { value: 'soil-drench', label: 'Soil Drench' },
    { value: 'root-dip', label: 'Root/Clone Dip' },
    { value: 'greenhouse-fog', label: 'Greenhouse Fogging' }
  ];

  const cropTypes = [
    { value: 'vegetables', label: 'Vegetables' },
    { value: 'herbs', label: 'Herbs' },
    { value: 'ornamentals', label: 'Ornamental Plants' },
    { value: 'cannabis', label: 'Cannabis' },
    { value: 'greenhouse-mixed', label: 'Mixed Greenhouse Crops' }
  ];

  const areaUnits = [
    { value: 'sqft', label: 'Square Feet' },
    { value: 'sqm', label: 'Square Meters' },
    { value: 'plants', label: 'Number of Plants' },
    { value: 'beds', label: 'Growing Beds' }
  ];

  const waterSources = [
    { value: 'municipal', label: 'Municipal Water' },
    { value: 'well', label: 'Well Water' },
    { value: 'ro', label: 'Reverse Osmosis' },
    { value: 'collected', label: 'Collected Rainwater' }
  ];

  const calculateChemicalApplication = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const selectedChemical = chemicals?.find(c => c?.value === formData?.chemical);
      const treatmentAreaNum = parseFloat(formData?.treatmentArea);
      const tankSizeNum = parseFloat(formData?.tankSize);

      if (!selectedChemical) {
        setIsCalculating(false);
        return;
      }

      // Get application rate based on method
      const methodKey = formData?.applicationMethod?.includes('foliar') ? 'foliar' :
                       formData?.applicationMethod?.includes('drench') ? 'drench' : 'dip';
      
      const rateRange = selectedChemical?.rates?.[methodKey]?.split('-') || ['1', '2'];
      const baseRate = parseFloat(rateRange?.[0]);
      const maxRate = parseFloat(rateRange?.[1] || rateRange?.[0]);

      // Calculate coverage area
      let coveragePerGallon = 1000; // sq ft per gallon for foliar
      if (formData?.applicationMethod?.includes('drench')) coveragePerGallon = 100;
      if (formData?.applicationMethod?.includes('dip')) coveragePerGallon = 2000;

      // Adjust for area unit
      let adjustedArea = treatmentAreaNum;
      if (formData?.areaUnit === 'sqm') adjustedArea = treatmentAreaNum * 10.764;
      if (formData?.areaUnit === 'plants') adjustedArea = treatmentAreaNum * 4; // Assume 4 sq ft per plant
      if (formData?.areaUnit === 'beds') adjustedArea = treatmentAreaNum * 32; // Assume 4x8 beds

      const totalSolutionNeeded = adjustedArea / coveragePerGallon;
      const chemicalAmount = totalSolutionNeeded * tankSizeNum * baseRate;
      const maxChemicalAmount = totalSolutionNeeded * tankSizeNum * maxRate;

      // Calculate costs (rough estimates)
      const costPerOz = getChemicalCost(formData?.chemical);
      const estimatedCost = chemicalAmount * costPerOz;

      // Compliance and timing recommendations
      const complianceInfo = getComplianceInfo(selectedChemical, formData?.cropType);
      const mixingInstructions = getMixingInstructions(selectedChemical, formData?.waterSource);

      const calculationResults = {
        chemicalAmount: chemicalAmount?.toFixed(2),
        chemicalAmountMax: maxChemicalAmount?.toFixed(2),
        totalSolution: totalSolutionNeeded?.toFixed(2),
        waterAmount: (totalSolutionNeeded * tankSizeNum - chemicalAmount)?.toFixed(2),
        estimatedCost: estimatedCost?.toFixed(2),
        coverageArea: adjustedArea,
        applicationRate: `${baseRate}-${maxRate} oz/gallon`,
        complianceInfo,
        mixingInstructions,
        warnings: generateChemicalWarnings()
      };

      setResults(calculationResults);
      
      // Save to history
      onCalculationComplete({
        chemical: selectedChemical?.label,
        applicationMethod: formData?.applicationMethod,
        treatmentArea: `${formData?.treatmentArea} ${formData?.areaUnit}`,
        chemicalAmount: calculationResults?.chemicalAmount,
        estimatedCost: calculationResults?.estimatedCost
      });

      setIsCalculating(false);
    }, 1200);
  };

  const getChemicalCost = (chemical) => {
    const costs = {
      'zerotol-2': 0.75,
      'azadirachtin': 1.25,
      'cease': 0.95,
      'rootshield-plus': 1.10,
      'suffoil-x': 0.60,
      'bt-thuricide': 0.85
    };
    return costs?.[chemical] || 1.00;
  };

  const getComplianceInfo = (chemical, cropType) => {
    const baseInfo = {
      omriListed: ['cease', 'rootshield-plus', 'bt-thuricide']?.includes(chemical?.value),
      phiDays: chemical?.value === 'azadirachtin' ? 1 : 0,
      reiHours: chemical?.value?.includes('oil') ? 12 : 4,
      restrictions: []
    };

    if (cropType === 'cannabis' && chemical?.value === 'zerotol-2') {
      baseInfo?.restrictions?.push('Stop applications 2 weeks before harvest');
    }

    if (chemical?.type === 'oil' && cropType === 'herbs') {
      baseInfo?.restrictions?.push('Avoid application during high heat periods');
    }

    return baseInfo;
  };

  const getMixingInstructions = (chemical, waterSource) => {
    const instructions = [];
    
    instructions?.push('1. Fill tank with 1/2 required water volume');
    instructions?.push(`2. Add ${chemical?.label} slowly while agitating`);
    
    if (waterSource === 'well') {
      instructions?.push('3. Test and adjust pH if necessary (well water often high pH)');
    }
    
    if (chemical?.phOptimal) {
      instructions?.push(`4. Maintain pH between ${chemical?.phOptimal} for optimal effectiveness`);
    }
    
    instructions?.push('5. Add remaining water to reach final volume');
    instructions?.push('6. Continue agitation during application');

    return instructions;
  };

  const generateChemicalWarnings = () => {
    const warnings = [];
    const selectedChemical = chemicals?.find(c => c?.value === formData?.chemical);
    
    if (formData?.phLevel && selectedChemical?.phOptimal) {
      const ph = parseFloat(formData?.phLevel);
      const optimalRange = selectedChemical?.phOptimal?.split('-')?.map(p => parseFloat(p));
      if (ph < optimalRange?.[0] || ph > optimalRange?.[1]) {
        warnings?.push('Water pH outside optimal range - may reduce effectiveness');
      }
    }

    if (formData?.waterSource === 'well') {
      warnings?.push('Well water may contain minerals that affect chemical performance');
    }

    if (formData?.applicationMethod === 'foliar-spray' && formData?.cropType === 'cannabis') {
      warnings?.push('Avoid foliar applications during flower stage');
    }

    return warnings;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setResults(null);
  };

  const resetForm = () => {
    setFormData({
      chemical: '',
      applicationMethod: 'foliar-spray',
      cropType: '',
      treatmentArea: '',
      areaUnit: 'sqft',
      tankSize: '1',
      concentrationTarget: '',
      waterSource: 'municipal',
      phLevel: ''
    });
    setResults(null);
  };

  const isFormValid = formData?.chemical && formData?.treatmentArea && formData?.cropType;

  return (
    <div className="space-y-6">
      {/* Input Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chemical Product *
            </label>
            <Select
              value={formData?.chemical}
              onChange={(value) => handleInputChange('chemical', value)}
              options={chemicals}
              placeholder="Select chemical product"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Application Method *
            </label>
            <Select
              value={formData?.applicationMethod}
              onChange={(value) => handleInputChange('applicationMethod', value)}
              options={applicationMethods}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Crop Type *
            </label>
            <Select
              value={formData?.cropType}
              onChange={(value) => handleInputChange('cropType', value)}
              options={cropTypes}
              placeholder="Select crop type"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Treatment Area *
              </label>
              <Input
                type="number"
                value={formData?.treatmentArea}
                onChange={(e) => handleInputChange('treatmentArea', e?.target?.value)}
                placeholder="Enter area"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <Select
                value={formData?.areaUnit}
                onChange={(value) => handleInputChange('areaUnit', value)}
                options={areaUnits}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tank Size (Gallons)
            </label>
            <Input
              type="number"
              value={formData?.tankSize}
              onChange={(e) => handleInputChange('tankSize', e?.target?.value)}
              placeholder="Tank capacity"
              min="0.25"
              step="0.25"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Water Source
            </label>
            <Select
              value={formData?.waterSource}
              onChange={(value) => handleInputChange('waterSource', value)}
              options={waterSources}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Water pH (Optional)
            </label>
            <Input
              type="number"
              value={formData?.phLevel}
              onChange={(e) => handleInputChange('phLevel', e?.target?.value)}
              placeholder="e.g., 6.5"
              min="4"
              max="10"
              step="0.1"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Regulatory Compliance</p>
                <p>Always follow product label rates and local regulations. This calculator provides guidance only.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          variant="outline"
          onClick={resetForm}
          className="border-gray-300"
        >
          Reset Form
        </Button>
        
        <Button
          onClick={calculateChemicalApplication}
          disabled={!isFormValid || isCalculating}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isCalculating ? (
            <>
              <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
              Calculating...
            </>
          ) : (
            <>
              <Icon name="Flask" size={16} className="mr-2" />
              Calculate Application
            </>
          )}
        </Button>
      </div>
      {/* Results */}
      {results && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Icon name="CheckCircle" size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-blue-900">Application Calculations</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{results?.chemicalAmount}-{results?.chemicalAmountMax} oz</div>
              <div className="text-sm text-gray-600">Chemical Amount</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{results?.totalSolution} gal</div>
              <div className="text-sm text-gray-600">Total Solution</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">${results?.estimatedCost}</div>
              <div className="text-sm text-gray-600">Estimated Cost</div>
            </div>
          </div>

          {/* Mixing Instructions */}
          <div className="mb-6">
            <h4 className="font-semibold text-blue-900 mb-3">Mixing Instructions</h4>
            <div className="bg-white rounded-lg p-4">
              <ol className="space-y-2">
                {results?.mixingInstructions?.map((instruction, index) => (
                  <li key={index} className="text-sm text-gray-700">{instruction}</li>
                ))}
              </ol>
            </div>
          </div>

          {/* Compliance Information */}
          <div className="mb-4">
            <h4 className="font-semibold text-blue-900 mb-3">Compliance Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={results?.complianceInfo?.omriListed ? "CheckCircle" : "XCircle"} 
                    size={16} 
                    className={results?.complianceInfo?.omriListed ? "text-green-600" : "text-gray-400"} 
                  />
                  <span className="text-sm font-medium">OMRI Listed</span>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="text-lg font-bold text-blue-600">{results?.complianceInfo?.phiDays}</div>
                <div className="text-xs text-gray-600">PHI Days</div>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="text-lg font-bold text-blue-600">{results?.complianceInfo?.reiHours}h</div>
                <div className="text-xs text-gray-600">REI Hours</div>
              </div>
            </div>
          </div>

          {/* Warnings */}
          {results?.warnings && results?.warnings?.length > 0 && (
            <div className="border-t border-blue-200 pt-4">
              <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                <Icon name="AlertTriangle" size={16} className="mr-2" />
                Important Considerations
              </h4>
              <ul className="space-y-1">
                {results?.warnings?.map((warning, index) => (
                  <li key={index} className="text-sm text-yellow-700">• {warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChemicalCalculator;